import { useMemo } from 'react';
import type {
  BusinessDataV2Type,
  BusinessUserV2Client,
  PayformCapabilityEnum,
  SingleUserProfileType,
} from '@am3nd-org/pointcraft-types';
import {
  intersectPayformGrants,
  DefaultBusinessRoleIdV2,
  BusinessAccessPolicyModeV2,
  evaluatePayformCapabilities,
} from '@am3nd-org/pointcraft-types';

import useUserDataStore from 'src/store/user';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useOperatorAuthenticationStore } from 'src/store/OperatorAuthentication';

/* =================== PAYFORMS ACCESS (ACCOUNT ACCESS V2, HIDE/SHOW ONLY) =================== */

/**
 * The frontend twin of the backend's centralized Payforms authorization helper
 * (PLAN §7.4, DEC-P2-03 revised, DEC-P2-11). It mirrors
 * `src/manager/_shared/hooks/use-manager-access.ts`:
 *
 *   effective grants = business ceiling (`businessData.access_v2.businessAccess`)
 *                      ∩ the acting user's role grants
 *   capabilities     = `evaluatePayformCapabilities(effective)` from the shared
 *                      types artifact — the SAME definition the backend uses.
 *
 * Rules (each one mirrors a backend step; the backend stays authoritative and
 * this hook only hides or shows navigation and controls):
 *  1. Principal — no identified, active user → nothing (DEC-P2-11: a
 *     business-level or unidentified session may not manage, read PII or
 *     cancel; VIEW likewise requires an identified user). Super-admin → all.
 *  2. Source — `businessData.access_v2` (server-resolved, already V2-shaped).
 *  3. Acting role — `currentUser.roleId`; a null/dangling id falls back to the
 *     legacy mapping used by the backend's `resolveUserRoleId`:
 *     store-admin / default user → `role_owner`, otherwise `role_cashier`.
 *  4. Effective grants — ceiling ∩ role; `BUSINESS_ONLY` policy → ceiling only.
 *  5. Capability — shared evaluator, never a local re-definition.
 *
 * A legacy-derived ceiling never contains `PAYFORMS`, so a business that was
 * not provisioned through the access-control tab sees no Payforms at all.
 */

export type PayformsCapabilities = Record<PayformCapabilityEnum, boolean>;

export type PayformsAccess = PayformsCapabilities & {
  /** True when an identified user could be resolved at all (for copy, not for gating). */
  identified: boolean;
};

/** The five facts the resolver needs about the acting user — nothing else. */
export type PayformsPrincipal = {
  roleId: string | null | undefined;
  isActive: boolean;
  isStoreAdmin?: boolean;
  isDefault?: boolean;
  isSuperAdmin?: boolean;
};

export const NO_PAYFORMS_ACCESS: PayformsAccess = {
  VIEW: false,
  MANAGE: false,
  PII: false,
  CANCEL: false,
  PAYMENT_SETUP: false,
  identified: false,
};

const ALL_PAYFORMS_ACCESS: PayformsAccess = {
  VIEW: true,
  MANAGE: true,
  PII: true,
  CANCEL: true,
  PAYMENT_SETUP: true,
  identified: true,
};

/** Backend `resolveUserRoleId` twin: store-admin / default → owner, else cashier. */
export function resolveLegacyRoleId(principal: PayformsPrincipal): string {
  return principal.isStoreAdmin || principal.isDefault
    ? DefaultBusinessRoleIdV2.OWNER
    : DefaultBusinessRoleIdV2.CASHIER;
}

type BusinessLike = Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null | undefined;

export function resolvePayformsAccess(
  principal: PayformsPrincipal | null | undefined,
  business: BusinessLike
): PayformsAccess {
  // 1. Principal: nothing without an identified, active user (DEC-P2-11).
  if (!principal || !principal.isActive) return NO_PAYFORMS_ACCESS;
  if (principal.isSuperAdmin || business?.isSuperAdmin) return ALL_PAYFORMS_ACCESS;

  // 2. Source: the server-resolved V2 authorization record.
  const authorization = business?.access_v2;
  if (!authorization?.businessAccess) return { ...NO_PAYFORMS_ACCESS, identified: true };

  const { businessAccess, roleDefinitions } = authorization;
  const mode = businessAccess.policy?.mode ?? BusinessAccessPolicyModeV2.HYBRID;

  // 4a. BUSINESS_ONLY: the ceiling is the whole answer.
  if (mode === BusinessAccessPolicyModeV2.BUSINESS_ONLY) {
    return { ...evaluatePayformCapabilities(businessAccess), identified: true };
  }

  // 3. Acting role, with the legacy null-roleId bridge.
  const roleId = principal.roleId && principal.roleId.length > 0 ? principal.roleId : null;
  const assignedRole =
    (roleId && roleDefinitions?.find((candidate) => candidate.id === roleId)) ||
    (roleId === null
      ? roleDefinitions?.find((candidate) => candidate.id === resolveLegacyRoleId(principal))
      : undefined);
  if (!assignedRole?.grants) return { ...NO_PAYFORMS_ACCESS, identified: true };

  // 4b/5. Ceiling ∩ role, evaluated by the shared definition.
  const effective = intersectPayformGrants(businessAccess, assignedRole.grants);
  return { ...evaluatePayformCapabilities(effective), identified: true };
}

/* =================== PRINCIPAL RESOLUTION (DASHBOARD) =================== */

/** Firebase uid prefix for Auth V3 human users (`user:{businessUserId}`). */
const USER_FIREBASE_UID_PREFIX = 'user:';

/**
 * Who is acting on the PointPay dashboard, in priority order:
 *  1. an Auth V3 human Firebase identity (`user:{id}`), matched to the synced user;
 *  2. the Manager-style profile (`profile.currentUser`) for legacy callers;
 *  3. the PIN-identified operator session, matched to the synced business user.
 * A business-account session (account number + code) with none of the above
 * is unidentified and gets nothing (DEC-P2-11).
 */
export function resolvePayformsPrincipal({
  profileUser,
  operator,
  authUid,
  businessUsers,
}: {
  profileUser: SingleUserProfileType | null | undefined;
  operator: { id: string; roleId: string | null } | null | undefined;
  authUid: string | null | undefined;
  businessUsers: BusinessUserV2Client[] | undefined;
}): PayformsPrincipal | null {
  // An authenticated human session owns the principal. A stale compatibility
  // profile or POS operator must never override the signed-in user's role.
  if (authUid?.startsWith(USER_FIREBASE_UID_PREFIX)) {
    const user = businessUsers?.find(
      (candidate) => candidate.id === authUid.slice(USER_FIREBASE_UID_PREFIX.length)
    );
    return user
      ? { roleId: user.roleId, isActive: user.isActive, isDefault: user.isDefault }
      : null;
  }

  if (profileUser) {
    return {
      roleId: profileUser.roleId ?? null,
      isActive: profileUser.isActive,
      isStoreAdmin: profileUser.isStoreAdmin,
      isDefault: profileUser.isDefault,
      isSuperAdmin: profileUser.isSuperAdmin,
    };
  }

  const userId = operator?.id;
  if (!userId) return null;

  const businessUser = businessUsers?.find((candidate) => candidate.id === userId);
  if (!businessUser) return null;

  return {
    roleId: operator?.roleId ?? businessUser.roleId ?? null,
    isActive: businessUser.isActive,
    isDefault: businessUser.isDefault,
  };
}

/* =================== HOOK =================== */

export function usePayformsAccess(): PayformsAccess {
  const business = useDeviceSyncStore((state) => state.businessData);
  const businessUsers = useDeviceSyncStore((state) => state.userData);
  const profileUser = useUserDataStore((state) => state.profile.currentUser);
  const operator = useOperatorAuthenticationStore((state) =>
    state.status === 'AUTHENTICATED' ? state.operator : null
  );
  const authUid = useAuthStore((state) => state.user?.uid ?? null);

  return useMemo(() => {
    const principal = resolvePayformsPrincipal({
      profileUser,
      operator,
      authUid,
      businessUsers,
    });
    return resolvePayformsAccess(principal, business);
  }, [profileUser, operator, authUid, businessUsers, business]);
}
